import React, { useEffect, useState } from 'react';
import { Row, Col, Typography, Skeleton } from 'antd';
import { useNavigate } from 'react-router-dom';
import PublicServ from '../service/PublicServ';
import { useCategory } from '../context/CategoryContext';
import { capitalizeFirst, renderSafeText } from "../util/unitFormatter";
import { LocationContext } from '../LocationContext';
import { usePreference } from '../context/PreferenceContext';

const { Title } = Typography;

// System-level or non-shopping category names that shouldn't appear
const EXCLUDED_CATEGORIES = ['cart', 'checkout', 'account', 'profile', 'order', 'orders'];

const CategoryFeaturedBanners = () => {
    const { t } = usePreference();
    const { categories } = useCategory();
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hiddenBanners, setHiddenBanners] = useState(new Set());
    const navigate = useNavigate();
    const { latitude, longitude, radius } = React.useContext(LocationContext);

    useEffect(() => {
        const fetchBanners = async () => {
            setLoading(true);
            try {
                const params = { hasCategory: true };
                if (latitude && longitude) {
                    params.lat = latitude;
                    params.lng = longitude;
                    params.radius = radius;
                }
                const res = await PublicServ.getFeaturedCategoryBanners(params);
                const rawBanners = res.data || [];

                const validBanners = rawBanners.filter(b => {
                    // Exclude mobile-only banners
                    if (b.targetPlatform && b.targetPlatform === 'mobile') return false;

                    // Prevent top hero banners from leaking into the Shop by Categories grid
                    if (b.position === 'HOME_RIGHT' || b.position === 'HOME_TOP_CAROUSEL' || b.position === 'HOME_TOP_SLIDER') return false;
                    if (b.position === 'DEFAULT' && b.type === 'global' && !b.category) return false;

                    const catName = (b.category?.name || '').toLowerCase().trim();
                    if (EXCLUDED_CATEGORIES.includes(catName)) return false;
                    // Keep even if imageUrl is missing — we'll try to use the category image
                    return true;
                });

                // Try to enrich banners with category imageUrl if banner has none
                let enriched = validBanners;
                if (validBanners.some(b => !b.imageUrl) && categories.length > 0) {
                    enriched = validBanners.map(b => {
                        if (!b.imageUrl) {
                            const match = categories.find(c => c._id === (b.category?._id || b.category));
                            return { ...b, imageUrl: match?.imageUrl || match?.image || null };
                        }
                        return b;
                    });
                }

                // Only keep banners that still have a valid image after enrichment
                setBanners(enriched.filter(b => b.imageUrl));
            } catch (err) {
                console.error("Error fetching category banners:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchBanners();
    }, [latitude, longitude, radius, categories]);

    const handleImageError = (bannerId) => {
        setHiddenBanners(prev => new Set([...prev, bannerId]));
    };

    const visibleBanners = banners.filter(b => !hiddenBanners.has(b._id));

    if (loading) {
        return (
            <div className="pcu-margin-bottom-40 pcu-w-100">
                <div className="shop-list-header">
                    <Skeleton.Input active className="pcu-w-200 pcu-h-24 pcu-margin-bottom-10" />
                </div>
                <Row gutter={[16, 16]}>
                    {[1, 2].map((i) => (
                        <Col xs={24} md={12} key={i}>
                            <div className="pcu-h-200 pcu-bg-muted pcu-radius-12 pcu-overflow-hidden pcu-flex pcu-justify-center pcu-flex-center-y">
                                <Skeleton.Image active className="pcu-w-100 pcu-h-100" />
                            </div>
                        </Col>
                    ))}
                </Row>
            </div>
        );
    }

    if (visibleBanners.length === 0) return null;

    return (
        <div className="pcu-margin-bottom-40 pcu-w-100">
            <div className="shop-list-header">
                <span>{t('shop_by_categories')}</span>
            </div>
            <Row gutter={[16, 16]}>
                {visibleBanners.map((banner) => (
                    <Col xs={24} md={12} key={banner._id}>
                        <div
                            className="category-promo-banner pcu-relative pcu-h-200 pcu-radius-12 pcu-overflow-hidden pcu-cursor-pointer pcu-shadow-md"
                            onClick={() => navigate(`/category/${encodeURIComponent(banner.category?.name || banner.title || '')}`)}
                            style={{ transition: 'transform 0.3s ease' }}
                            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.02)'}
                            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                        >
                            <img
                                src={banner.imageUrl}
                                alt={banner.title}
                                className="pcu-w-100 pcu-h-100 pcu-image-cover"
                                onError={() => handleImageError(banner._id)}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                            <div
                                className="pcu-absolute pcu-bottom-0 pcu-left-0 pcu-w-100 pcu-padding-20 pcu-color-white"
                                style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.7))' }}
                            >
                                <Title level={4} className="pcu-color-white pcu-margin-0">{banner.title}</Title>
                                {banner.category?.name && (
                                    <span className="pcu-font-12 pcu-bg-danger pcu-padding-x-8 pcu-padding-y-2 pcu-radius-4" style={{ textTransform: 'capitalize' }}>
                                        {renderSafeText(banner.category.name)}
                                    </span>
                                )}
                            </div>
                        </div>
                    </Col>
                ))}
            </Row>
        </div>
    );
};

export default CategoryFeaturedBanners;
